/**
 * Lazily resolve the authoring host window that exposes a given preview API.
 *
 * Unlike a module-level singleton resolved once at import time, this walks the
 * same-origin window candidates on every call. That matters because the author
 * shell assigns its `window.*` preview handlers (acceptFromPreview,
 * acceptPatchesFromPreview, acceptHiddenFromPreview, …) at runtime during
 * component setup — after the preview iframe's modules have already been
 * imported. A singleton resolved at import time can miss those handlers and
 * cache the wrong (topmost) same-origin window, so callers silently fall back
 * to the runtime-store-only path and their edits never reach the Monaco draft.
 *
 * Returns undefined outside authoring.
 *
 * @param method the EditorWindow handler the caller is about to invoke; used as
 *   the match predicate so the resolved window is guaranteed to expose it.
 */
export const getEditorWindow = (
  method: keyof EditorWindow
): EditorWindow | undefined => {
  if (!__AUTHORING__) return;

  const candidates: Window[] = [];
  const add = (candidate: Window | null | undefined) => {
    if (candidate && !candidates.includes(candidate))
      candidates.push(candidate);
  };

  try {
    add(window);
  } catch {}
  try {
    add(window.parent);
  } catch {}
  try {
    add(window.top);
  } catch {}
  try {
    add(window.opener);
  } catch {}
  try {
    add(window.top?.opener);
  } catch {}

  for (const candidate of candidates) {
    try {
      if (candidate.location.origin !== window.location.origin) continue;
      if (method in candidate) return candidate as EditorWindow;
    } catch {}
  }
};

export default getEditorWindow;
